import { type EnvironmentId } from "@cmmd-forge/contracts";
import { createKnownEnvironment } from "@cmmd-forge/client-runtime";
import { fetchRemoteEnvironmentDescriptor } from "../remote/api";
import { getPrimaryKnownEnvironment } from "../primary";
import { createEnvironmentConnection, type EnvironmentConnection } from "./connection";
import {
  createDesktopLocalEnvironmentClient,
  createPrimaryEnvironmentClient,
  readDesktopCloudAuthTarget,
  readDesktopLocalAuthTarget,
} from "./environmentConnectionClients";
import { createEnvironmentConnectionHandlers } from "./environmentConnectionHandlers";
import {
  setRuntimeConnecting,
  setRuntimeConnected,
  setRuntimeDisconnected,
} from "./runtimeConnectionStatus";
import { useSavedEnvironmentRuntimeStore } from "./savedEnvironmentRuntimeStore";

export type EnvironmentConnectionFactoryDependencies = {
  readonly readConnection: (environmentId: EnvironmentId) => EnvironmentConnection | null;
  readonly registerConnection: (connection: EnvironmentConnection) => EnvironmentConnection;
};

function isoNow(): string {
  return new Date().toISOString();
}

export function createPrimaryEnvironmentConnection(
  dependencies: EnvironmentConnectionFactoryDependencies,
): EnvironmentConnection {
  const knownEnvironment = getPrimaryKnownEnvironment();
  if (!knownEnvironment?.environmentId) {
    throw new Error("Unable to resolve the primary environment.");
  }

  const existing = dependencies.readConnection(knownEnvironment.environmentId);
  if (existing) {
    return existing;
  }

  const cloudAuthTarget = readDesktopCloudAuthTarget();
  const shouldOpenReadinessStream =
    knownEnvironment.source === "paired-bearer" || !import.meta.env.DEV;

  return dependencies.registerConnection(
    createEnvironmentConnection({
      kind: "primary",
      knownEnvironment,
      client: createPrimaryEnvironmentClient(knownEnvironment),
      resolveEnvironmentId: () =>
        getPrimaryKnownEnvironment()?.environmentId ?? knownEnvironment.environmentId,
      ...(shouldOpenReadinessStream
        ? { readiness: cloudAuthTarget ? { bearerToken: cloudAuthTarget.bearerToken } : {} }
        : {}),
      ...createEnvironmentConnectionHandlers(),
    }),
  );
}

export async function createDesktopLocalEnvironmentConnection(
  dependencies: EnvironmentConnectionFactoryDependencies,
): Promise<EnvironmentConnection> {
  const target = readDesktopLocalAuthTarget();
  if (!target) {
    throw new Error(
      "Desktop Local runtime is not authenticated. Sign in with CMMD again before importing local projects.",
    );
  }

  const descriptor = await fetchRemoteEnvironmentDescriptor({
    httpBaseUrl: target.httpBaseUrl,
    bearerToken: target.bearerToken,
  });
  const existing = dependencies.readConnection(descriptor.environmentId);
  if (existing) {
    return existing;
  }

  const knownEnvironment = createKnownEnvironment({
    id: descriptor.environmentId,
    label: target.label,
    source: "desktop-managed",
    target: {
      httpBaseUrl: target.httpBaseUrl,
      wsBaseUrl: target.wsBaseUrl,
    },
  });

  return dependencies.registerConnection(
    createEnvironmentConnection({
      kind: "desktop-local",
      knownEnvironment: {
        ...knownEnvironment,
        environmentId: descriptor.environmentId,
        label: descriptor.label,
      },
      client: createDesktopLocalEnvironmentClient({
        environmentId: descriptor.environmentId,
        httpBaseUrl: target.httpBaseUrl,
        wsBaseUrl: target.wsBaseUrl,
        bearerToken: target.bearerToken,
        onConnecting: setRuntimeConnecting,
        onConnected: setRuntimeConnected,
        onError: (environmentId, message) => {
          useSavedEnvironmentRuntimeStore.getState().patch(environmentId, {
            connectionState: "error",
            lastError: message,
            lastErrorAt: isoNow(),
          });
        },
        onDisconnected: setRuntimeDisconnected,
      }),
      ...createEnvironmentConnectionHandlers(),
    }),
  );
}
